// src/components/providerAssignments/AssignmentsTable.tsx
//
// Reusable assignments table used by Department Assignments tab,
// Contact Assigned Providers section, and the Provider workload panel.
// Columns are configurable so each surface can hide the fields it
// already contextualizes (e.g., Contact section already knows the
// person, so hides that column).

import type { PersonProviderAssignment } from '../../types/api';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  contactLabel,
  formatDate,
  providerLabel,
} from './assignmentDisplay';
import { PriorityChip } from './PriorityChip';
import { StatusChip } from './StatusChip';
import AssignmentRowActions from './AssignmentRowActions';

export type AssignmentColumn =
  | 'person'
  | 'provider'
  | 'department'
  | 'role'
  | 'priority'
  | 'status'
  | 'startDate'
  | 'endDate'
  | 'assignedBy';

export interface AssignmentsTableProps {
  rows: PersonProviderAssignment[];
  columns?: AssignmentColumn[];
  emptyText?: string;
  onChanged?: () => void;
  /** When set, a data-testid attribute is added for scoped queries. */
  testId?: string;
}

const DEFAULT_COLUMNS: AssignmentColumn[] = [
  'person',
  'provider',
  'role',
  'priority',
  'status',
  'startDate',
  'endDate',
  'assignedBy',
];

export default function AssignmentsTable({
  rows,
  columns = DEFAULT_COLUMNS,
  emptyText = 'No assignments',
  onChanged,
  testId,
}: AssignmentsTableProps) {
  if (rows.length === 0) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs text-slate-500"
        data-testid={testId ?? 'assignments-empty'}
      >
        {emptyText}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid={testId ?? 'assignments-table'}>
      <table className="min-w-full text-xs">
        <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
          <tr>
            {columns.includes('person') && <Th>Person</Th>}
            {columns.includes('provider') && <Th>Provider</Th>}
            {columns.includes('department') && <Th>Department</Th>}
            {columns.includes('role') && <Th>Role</Th>}
            {columns.includes('priority') && <Th>Priority</Th>}
            {columns.includes('status') && <Th>Status</Th>}
            {columns.includes('startDate') && <Th>Start</Th>}
            {columns.includes('endDate') && <Th>End</Th>}
            {columns.includes('assignedBy') && <Th>Assigned by</Th>}
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-50" id={`assignment-${row.id}`}>
              {columns.includes('person') && (
                <Td className="font-medium text-slate-900">{contactLabel(row.contact)}</Td>
              )}
              {columns.includes('provider') && (
                <Td className="text-slate-900">{providerLabel(row.provider)}</Td>
              )}
              {columns.includes('department') && (
                <Td className="text-slate-700">{row.departmentCircle?.name ?? '—'}</Td>
              )}
              {columns.includes('role') && (
                <Td className="text-slate-700">{row.providerRole?.name ?? '—'}</Td>
              )}
              {columns.includes('priority') && (
                <Td>
                  <PriorityChip priority={row.priority} />
                  <span className="sr-only">{PRIORITY_LABELS[row.priority]}</span>
                </Td>
              )}
              {columns.includes('status') && (
                <Td>
                  <StatusChip status={row.status} />
                  <span className="sr-only">{STATUS_LABELS[row.status]}</span>
                </Td>
              )}
              {columns.includes('startDate') && (
                <Td className="text-slate-600">{formatDate(row.startDate)}</Td>
              )}
              {columns.includes('endDate') && (
                <Td className="text-slate-600">{formatDate(row.endDate)}</Td>
              )}
              {columns.includes('assignedBy') && (
                <Td className="text-slate-600">{providerLabel(row.assignedBy)}</Td>
              )}
              <Td className="text-right">
                <AssignmentRowActions assignment={row} onChanged={onChanged} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={`px-3 py-2 text-left font-medium uppercase tracking-wider ${className ?? ''}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`whitespace-nowrap px-3 py-2 ${className ?? ''}`}>{children}</td>;
}
